import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";
import { Logo } from "@/components/brand/logo";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PlanChooser } from "@/components/onboarding/PlanChooser";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { useSession } from "@/hooks/auth-hooks";
import { useSubscription } from "@/hooks/use-subscription";
import { parseChoosePlanRedirect } from "@/lib/internal-redirect";

export const Route = createFileRoute("/choose-plan")({
	validateSearch: (search: Record<string, unknown>) => ({
		redirect: parseChoosePlanRedirect(search.redirect) ?? undefined,
		reason:
			search.reason === "subscription_required"
				? "subscription_required"
				: undefined,
	}),
	component: ChoosePlanPage,
	head: () => ({
		meta: [{ title: "AgentReacher | Choose your plan" }],
	}),
});

function ChoosePlanPage() {
	const navigate = useNavigate();
	const { redirect, reason } = useSearch({ from: "/choose-plan" });
	const { user, isPending } = useSession();
	const { isActive, isLoading: isSubscriptionLoading } = useSubscription();
	const needsCheckout = !isActive;

	useEffect(() => {
		if (reason === "subscription_required") return;
		if (typeof window === "undefined") return;

		const params = new URLSearchParams(window.location.search);
		if (!params.has("redirect") && !params.has("reason")) return;

		const redirectMatches = redirect
			? params.get("redirect") === redirect
			: !params.has("redirect");
		const reasonMatches = reason
			? params.get("reason") === reason
			: !params.has("reason");

		if (redirectMatches && reasonMatches) return;

		navigate({
			to: "/choose-plan",
			search: { redirect, reason },
			replace: true,
		});
	}, [navigate, reason, redirect]);

	useEffect(() => {
		if (reason !== "subscription_required") return;
		toast.warning("Choose a plan to access the dashboard.");
		navigate({
			to: "/choose-plan",
			search: { redirect, reason: undefined },
			replace: true,
		});
	}, [navigate, reason, redirect]);

	useEffect(() => {
		if (isPending || isSubscriptionLoading || !user || needsCheckout) return;
		if (redirect) {
			window.location.assign(redirect);
			return;
		}
		navigate({ to: "/dashboard", replace: true });
	}, [
		isPending,
		isSubscriptionLoading,
		navigate,
		needsCheckout,
		redirect,
		user,
	]);

	if (isPending || (!!user && isSubscriptionLoading)) {
		return <LoadingScreen />;
	}

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<main className="flex-1">
				<div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden">
						<section className="rounded-md bg-white dark:bg-black">
							<div className="mx-auto flex w-full max-w-5xl flex-col px-4 py-10 sm:px-6 sm:py-14">
								<div className="mx-auto w-full max-w-3xl">
									<div className="mb-7 flex flex-col items-center text-center">
										<div className="relative mb-4 flex items-center justify-center">
											<div
												aria-hidden
												className="absolute inset-0 -z-10 rounded-full bg-primary/30 blur-2xl"
											/>
											<Logo size="md" disableLink showText={false} />
										</div>
										<h1 className="text-2xl font-semibold tracking-[-0.04em] sm:text-3xl">
											Choose your plan
										</h1>
										<p className="mt-2 text-sm leading-6 text-muted-foreground">
											Subscribe today. Not satisfied? Full refund within 30
											days.
										</p>
									</div>
									<PlanChooser redirect={redirect} />
								</div>
							</div>
						</section>
					</div>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
